import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { MagicWand, Check } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { tc } from "../../lib/theme";

const VARS = [
  { key: "flux_density_t", label: "Flux density (T)", lo: 1.45, hi: 1.8 },
  { key: "j_hv", label: "J HV (A/mm²)", lo: 2.0, hi: 4.0 },
  { key: "j_lv", label: "J LV (A/mm²)", lo: 2.0, hi: 4.2 },
  { key: "core_steps", label: "Core steps", lo: 4, hi: 9 },
  { key: "window_ratio", label: "Window ratio", lo: 0.8, hi: 1.3 },
];

const OBJECTIVES = [
  { key: "too", label: "Minimize Total Owning Cost (material + capitalized losses)" },
  { key: "weight", label: "Minimize Weight" },
  { key: "losses", label: "Minimize Losses" },
  { key: "dimensions", label: "Minimize Dimensions (tank volume)" },
];

const LABELS = {
  flux_density_t: "Flux density (T)", j_hv: "J HV (A/mm²)", j_lv: "J LV (A/mm²)",
  core_steps: "Core steps", window_ratio: "Window ratio",
};

const niceLabel = (k) => LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function OptimizeModal({ projectId, inputs, specs, rules, searchBounds, onClose, onApplied }) {
  const [objective, setObjective] = useState("too");
  // the knobs and their ranges come from the pack that is actually loaded, so a distribution
  // transformer, a reactor or a motor each get their own variables rather than the PT list
  const vars = Object.keys(searchBounds || {}).length
    ? Object.entries(searchBounds).map(([key, [lo, hi]]) => ({ key, label: niceLabel(key), lo, hi }))
    : VARS;
  const [bounds, setBounds] = useState(Object.fromEntries(vars.map((v) => [v.key, [v.lo, v.hi]])));
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const run = async () => {
    try {
      const { data } = await api.post(`/design/${projectId}/optimize`, {
        objective, specs, bounds: Object.fromEntries(Object.entries(bounds).map(([k, [a, b]]) => [k, [Number(a), Number(b)]])),
      });
      setJob({ status: "running", progress: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const jr = await api.get(`/design/jobs/${data.job_id}`);
          setJob(jr.data);
          if (jr.data.status !== "running") clearInterval(pollRef.current);
        } catch { clearInterval(pollRef.current); }
      }, 1200);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const applyBest = async () => {
    const best = job?.result?.best_inputs;
    if (!best) return;
    try {
      await api.post(`/design/${projectId}/versions`, {
        label: `Optimization: min ${objective} (TOO $${job.result.best_metrics.too_usd.toLocaleString()})`,
        inputs: { ...inputs, ...best }, results_summary: job.result.best_metrics, source: "optimization",
      });
      toast.success("Optimized design saved as version and applied");
      onApplied(best);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const result = job?.result;
  const pareto = result?.pareto || [];
  const scatter = (() => {
    if (!pareto.length) return "";
    const costs = pareto.map((p) => p.too_usd);
    const losses = pareto.map((p) => p.total_losses_kw);
    const [cMin, cMax] = [Math.min(...costs), Math.max(...costs)];
    const [lMin, lMax] = [Math.min(...losses), Math.max(...losses)];
    const W = 420, H = 220, px = 34, py = 24;
    const X = (c) => px + ((c - cMin) / Math.max(cMax - cMin, 1)) * (W - px - 14);
    const Y = (l) => H - py - ((l - lMin) / Math.max(lMax - lMin, 1)) * (H - py - 14);
    let s = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" font-family="JetBrains Mono, monospace" style="max-width:100%">`;
    s += `<line x1="${px}" y1="${H - py}" x2="${W - 10}" y2="${H - py}" stroke="${tc("border")}"/><line x1="${px}" y1="10" x2="${px}" y2="${H - py}" stroke="${tc("border")}"/>`;
    s += `<text x="${W / 2}" y="${H - 6}" font-size="9" fill="${tc("muted")}" text-anchor="middle">Total owning cost ($)</text>`;
    s += `<text x="10" y="${H / 2}" font-size="9" fill="${tc("muted")}" text-anchor="middle" transform="rotate(-90 10 ${H / 2})">Losses (kW)</text>`;
    pareto.forEach((p) => {
      const hot = p.is_top;
      s += `<circle cx="${X(p.too_usd).toFixed(1)}" cy="${Y(p.total_losses_kw).toFixed(1)}" r="${hot ? 4 : 2}" fill="${hot ? "var(--c-green)" : "var(--c-accent)"}" fill-opacity="${hot ? 1 : 0.45}"/>`;
    });
    return s + "</svg>";
  })();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="optimize-modal">
        <DialogHeader>
          <DialogTitle className="text-[var(--c-text)]">Auto-Optimize · {specs.rating_kva} kVA / {specs.hv_rated_kv}–{specs.lv_rated_kv} kV</DialogTitle>
          <DialogDescription className="sr-only">Differential-evolution design optimization</DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Objective</Label>
              <Select value={objective} onValueChange={setObjective}>
                <SelectTrigger data-testid="opt-objective" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                  {OBJECTIVES.map((o) => <SelectItem key={o.key} value={o.key} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[var(--c-muted)] text-xs">Variable ranges</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {vars.map((v) => (
                  <div key={v.key} className="flex items-center gap-2 bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2" data-testid={`opt-var-${v.key}`}>
                    <span className="text-xs text-[var(--c-text)] flex-1">{v.label}</span>
                    <Input type="number" step="any" data-testid={`opt-min-${v.key}`} value={bounds[v.key][0]}
                      onChange={(e) => setBounds({ ...bounds, [v.key]: [e.target.value, bounds[v.key][1]] })}
                      className="w-20 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs font-mono" />
                    <span className="text-[var(--c-muted)] text-xs">–</span>
                    <Input type="number" step="any" data-testid={`opt-max-${v.key}`} value={bounds[v.key][1]}
                      onChange={(e) => setBounds({ ...bounds, [v.key]: [bounds[v.key][0], e.target.value] })}
                      className="w-20 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-xs font-mono" />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-3 text-[11px] text-[var(--c-muted)]" data-testid="opt-constraints">
              Constraints: impedance {specs.impedance_pct}% ± tol · NLL ≤ {specs.no_load_loss_w} W · LL ≤ {specs.load_loss_75c_w} W · hot spot ≤ 118 °C
              {rules.length > 0 && ` · ${rules.length} client rules`}
            </div>
            <Button data-testid="opt-run-btn" onClick={run} disabled={job?.status === "running"}
              className="w-full bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm">
              <MagicWand size={14} className="mr-1.5" /> {job?.status === "running" ? "Optimizing…" : "Run Optimization"}
            </Button>
            {job && (
              <div data-testid="opt-progress">
                <div className="flex justify-between text-[10px] font-mono text-[var(--c-muted)] mb-1">
                  <span>{job.status === "running" ? "DIFFERENTIAL EVOLUTION RUNNING" : job.status.toUpperCase()}</span>
                  <span>{job.progress}%</span>
                </div>
                <div className="h-1.5 bg-[var(--c-border)] rounded-sm overflow-hidden">
                  <div className="h-full bg-[var(--c-green)] transition-all duration-300" style={{ width: `${job.progress}%` }} />
                </div>
                {job.status === "failed" && <div className="text-xs text-[var(--c-red)] mt-2">{job.error}</div>}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="opt-results">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[var(--c-muted)]">{result.candidates_evaluated} candidates evaluated · objective: {result.objective}</span>
              <Button size="sm" variant="outline" onClick={() => setJob(null)}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] h-7 text-xs rounded-sm">New Run</Button>
            </div>

            <div className="bg-[var(--c-bg)] border border-[var(--c-green)]/40 rounded-sm p-4" data-testid="opt-best">
              <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-green)] mb-2">Best candidate</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-[var(--c-text)]">
                {Object.entries(result.best_inputs).map(([k, v]) => <span key={k}>{k}=<span className="text-[var(--c-accent)]">{v}</span></span>)}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-[var(--c-muted)] mt-2">
                <span>TOO <span className="text-[var(--c-text)]">${result.best_metrics.too_usd.toLocaleString()}</span></span>
                <span>losses <span className="text-[var(--c-text)]">{result.best_metrics.total_losses_kw} kW</span></span>
                <span>weight <span className="text-[var(--c-text)]">{result.best_metrics.total_weight_kg} kg</span></span>
                <span>Z <span className="text-[var(--c-text)]">{result.best_metrics.impedance_pct}%</span></span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-3" data-testid="opt-pareto">
                <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">Pareto · cost vs losses</div>
                <div dangerouslySetInnerHTML={{ __html: scatter }} />
              </div>
              <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm p-3" data-testid="opt-sensitivity">
                <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">Sensitivity (Δ TOO %, ±8%)</div>
                {(result.sensitivity || []).map((s) => (
                  <div key={s.variable} className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-[10px] text-[var(--c-muted)] w-28 shrink-0">{s.variable}</span>
                    <div className="flex-1 h-2 bg-[var(--c-border)] rounded-sm overflow-hidden">
                      <div className="h-full bg-[var(--c-amber)]" style={{ width: `${Math.min(s.delta_too_pct * 8, 100)}%` }} />
                    </div>
                    <span className="font-mono text-[10px] text-[var(--c-text)] w-12 text-right">{s.delta_too_pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm overflow-x-auto" data-testid="opt-top5">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--c-border)]">
                    {["#", ...Object.keys(result.best_inputs), "TOO $", "Losses kW", "Weight kg"].map((h) => (
                      <th key={h} className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.top5.map((p, i) => (
                    <tr key={i} className="border-b border-[var(--c-border)]/50 last:border-0" data-testid={`opt-top5-row-${i}`}>
                      <td className="px-3 py-2 font-mono text-[var(--c-green)]">{i + 1}</td>
                      {Object.keys(result.best_inputs).map((k) => (
                        <td key={k} className="px-3 py-2 font-mono text-[var(--c-text)]">{p.inputs[k]}</td>
                      ))}
                      <td className="px-3 py-2 font-mono text-[var(--c-accent)]">{p.too_usd.toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-text)]">{p.total_losses_kw}</td>
                      <td className="px-3 py-2 font-mono text-[var(--c-text)]">{p.total_weight_kg}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button data-testid="opt-apply-btn" onClick={applyBest}
              className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
              <Check size={14} className="mr-1.5" weight="bold" /> Apply Design (saves version + recalculates)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
